import React from "react";

import { BlockStandard } from "components/admin/BlockStandard";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { PhotoUploadGrid } from "./PhotoUploadGrid";
import {
  LightingTypes,
  LightActivationType,
  LightCeilingType,
  MaintenanceState,
} from "api/automationTypes";
import { DeleteCTA } from "components/MUIExtensions/DeleteCTA";

const USAGE_TIMES = ["2h", "4h", "8h", "12h"];
const ACTIVATION_TYPES = Object.values(LightActivationType);
const CEILING_TYPES = Object.values(LightCeilingType);
const CONSERVATION_STATES = Object.values(MaintenanceState);

export const LightDetail = ({
  onChange,
  onDelete,
  lightData,
  itemPos,
  lampOptions,
}: LightDetailProps) => {
  const handleLampChange = ({ target }) => {
    const updatedLight = {
      ...lightData,
      type: target.value,
    };
    onChange(updatedLight);
  };

  const handleAmountChange = ({ target }) => {
    const updatedLight = {
      ...lightData,
      amount: target.value,
    };
    onChange(updatedLight);
  };

  const handlePowerChange = ({ target }) => {
    const updatedLight = {
      ...lightData,
      power: target.value,
    };
    onChange(updatedLight);
  };

  const handleActivationTypeChange = ({ target }) => {
    const updatedLight = {
      ...lightData,
      activation_type: target.value,
    };
    onChange(updatedLight);
  };

  const handleCeilingTypeChange = ({ target }) => {
    const updatedLight = {
      ...lightData,
      ceiling_type: target.value,
    };
    onChange(updatedLight);
  };

  const handleUsageChange = ({ target }) => {
    const updatedLight = {
      ...lightData,
      usage_time: target.value,
    };
    onChange(updatedLight);
  };

  const handleConservationStateChange = ({ target }) => {
    const updatedLight = {
      ...lightData,
      conservation_state: target.value,
    };
    onChange(updatedLight);
  };

  const handleObsChange = ({ target }) => {
    const updatedLight = {
      ...lightData,
      obs: target.value,
    };
    onChange(updatedLight);
  };

  const handlePhotosChange = (photos: File[]) => {
    const updatedLight = {
      ...lightData,
      photos: photos,
    };
    onChange(updatedLight);
  };

  return (
    <BlockStandard title={`Detalhar iluminação (${itemPos + 1})`}>
      <FormControl fullWidth>
        <InputLabel id={`select-lamp-${itemPos}`}>
          Tipo de Lâmpada / Luminária
        </InputLabel>
        <Select
          labelId={`select-lamp-${itemPos}`}
          label="Tipo de Lâmpada / Luminária"
          value={lightData.type || ""}
          onChange={handleLampChange}
        >
          {lampOptions?.map(({ id, name }) => (
            <MenuItem value={id} key={id}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <TextField
          label="Quantidade"
          value={lightData.amount || ""}
          onChange={handleAmountChange}
          sx={{ flex: 1 }}
        />

        <TextField
          label="Potência Unitária (W)"
          value={lightData.power || ""}
          onChange={handlePowerChange}
          sx={{ flex: 1 }}
        />
      </Grid>

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id={`select-activation-${itemPos}`}>
            Tipo de Acionamento
          </InputLabel>
          <Select
            labelId={`select-activation-${itemPos}`}
            label="Tipo de Acionamento"
            value={lightData.activation_type || ""}
            onChange={handleActivationTypeChange}
          >
            {ACTIVATION_TYPES?.map((option) => (
              <MenuItem value={option} key={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ flex: 1 }}>
          <InputLabel id={`select-ceiling-${itemPos}`}>
            Tipo de Teto/Forro
          </InputLabel>
          <Select
            labelId={`select-ceiling-${itemPos}`}
            label="Tipo de Teto/Forro"
            value={lightData.ceiling_type || ""}
            onChange={handleCeilingTypeChange}
          >
            {CEILING_TYPES?.map((option) => (
              <MenuItem value={option} key={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <FormControl fullWidth>
        <InputLabel id={`select-usage-${itemPos}`}>
          Tempo médio de uso diário
        </InputLabel>
        <Select
          labelId={`select-usage-${itemPos}`}
          label="Tempo médio de uso diário"
          value={lightData.usage_time || ""}
          onChange={handleUsageChange}
        >
          {USAGE_TIMES?.map((option) => (
            <MenuItem value={option} key={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel id={`select-conservation-${itemPos}`}>
          Estado de conservação
        </InputLabel>
        <Select
          labelId={`select-conservation-${itemPos}`}
          label="Estado de conservação"
          value={lightData.conservation_state || ""}
          onChange={handleConservationStateChange}
        >
          {CONSERVATION_STATES?.map((option) => (
            <MenuItem value={option} key={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Observações"
        value={lightData.obs || ""}
        onChange={handleObsChange}
        sx={{ mb: 2 }}
      />

      <PhotoUploadGrid
        photos={lightData.photos}
        onPhotosChange={handlePhotosChange}
        buttonText="Enviar foto"
      />

      <DeleteCTA onDeleteConfirmed={onDelete} />
    </BlockStandard>
  );
};

export type LightDetailProps = {
  onChange: (updatedItem: LightDetailItem) => void;
  onDelete: () => void;
  lightData: LightDetailItem;
  itemPos: number;
  lampOptions: LightingTypes[];
};

export type LightDetailItem = {
  id: string;
  type: string;
  amount: string;
  power: string;
  activation_type?: string;
  ceiling_type?: string;
  usage_time: string;
  conservation_state?: string;
  obs?: string;
  photos?: File[];
};

export const emptyLightDetail: LightDetailItem = {
  id: "",
  type: "",
  amount: "",
  power: "",
  activation_type: "",
  ceiling_type: "",
  usage_time: "",
  conservation_state: "",
  obs: "",
  photos: [],
};
